import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import DrillLogMapPreview from './DrillLogMapPreview';

const DrillLogDetail = ({ showToast }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [drillLog, setDrillLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchDrillLogDetail();
  }, [id]);

  const fetchDrillLogDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/drill_logs/${id}`);
      if (response.data.data) {
        setDrillLog(response.data.data);
      }
    } catch (error) {
      console.error('시추주상도 상세 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 일괄 표준서식 다운로드
  const handleBulkDownload = async () => {
    if (!drillLog || !drillLog.boreholes || drillLog.boreholes.length === 0) {
      if (showToast) showToast('다운로드할 시추공이 없습니다.');
      return;
    }

    setIsDownloading(true);
    const token = localStorage.getItem('token');
    const boreholeIds = drillLog.boreholes.map(b => b.id);

    try {
      const response = await axios.post('/boreholes/export-bulk', boreholeIds, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${drillLog.name}_표준서식_일괄다운로드.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (showToast) showToast('표준서식 일괄 다운로드가 완료되었습니다!');
    } catch (error) {
      console.error('일괄 다운로드 실패:', error);
      if (showToast) showToast('일괄 다운로드에 실패했습니다.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!drillLog) {
    return (
      <div className="flex-1 flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <p className="text-secondary-600">데이터를 찾을 수 없습니다</p>
          <button
            onClick={() => navigate('/drill-logs')}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-secondary-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* 헤더 */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/drill-logs')}
            className="flex items-center space-x-2 text-secondary-600 hover:text-secondary-900 mb-4 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">목록으로 돌아가기</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-secondary-900 mb-2">{drillLog.name}</h1>
              <p className="text-secondary-600">시추 주상도 상세 정보</p>
            </div>
            <button
              onClick={handleBulkDownload}
              disabled={isDownloading || !drillLog.boreholes || drillLog.boreholes.length === 0}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center space-x-2 ${
                isDownloading || !drillLog.boreholes || drillLog.boreholes.length === 0
                  ? 'bg-secondary-300 text-secondary-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:shadow-hard'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{isDownloading ? '다운로드 중...' : '표준서식 일괄 다운로드'}</span>
            </button>
          </div>
        </div>

        {/* 정보 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600 mb-1">시추공 개수</p>
                <p className="text-3xl font-bold text-secondary-900">{drillLog.borehole_count}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600 mb-1">생성일시</p>
                <p className="text-lg font-bold text-secondary-900">
                  {new Date(drillLog.created_at).toLocaleDateString('ko-KR')}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600 mb-1">문서 ID</p>
                <p className="text-lg font-bold text-secondary-900">#{drillLog.id}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* 시추공 위치 지도 */}
        <DrillLogMapPreview
          boreholes={drillLog.boreholes}
          onBoreholeClick={(borehole) => navigate(`/boreholes/${borehole.id}`)}
        />

        {/* 시추공 목록 */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="px-6 py-4 border-b border-secondary-200">
            <h2 className="text-lg font-bold text-secondary-900">시추공 목록</h2>
          </div>

          {!drillLog.boreholes || drillLog.boreholes.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-secondary-600">등록된 시추공이 없습니다</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      이름
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      좌표 (X, Y)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      지하수위
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      지반 종류
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      액상화 평가
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {drillLog.boreholes.map((borehole) => (
                    <tr
                      key={borehole.id}
                      onClick={() => navigate(`/boreholes/${borehole.id}`)}
                      className="hover:bg-secondary-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        #{borehole.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-secondary-900">{borehole.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {borehole.x.toFixed(2)}, {borehole.y.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {borehole.ground_water_table ? `${borehole.ground_water_table.toFixed(2)}m` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                          borehole.soil_type
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-secondary-100 text-secondary-600'
                        }`}>
                          {borehole.soil_type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                          borehole.liquefaction_assessment === '안전'
                            ? 'bg-green-100 text-green-700'
                            : borehole.liquefaction_assessment === '평가 필요'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-secondary-100 text-secondary-600'
                        }`}>
                          {borehole.liquefaction_assessment || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrillLogDetail;
